<script lang="ts">
  interface Props {
    presentation: {
      title: string;
      description?: string;
      slug: string;
      raw_url: string;
      user: {
        display_name: string;
        username: string;
      };
    };
  }

  type AspectRatio = '16:9' | '4:3' | '9:16' | 'auto';

  const RATIOS: Array<{ key: AspectRatio; label: string; css: string }> = [
    { key: '16:9',  label: '16:9',  css: '16/9' },
    { key: '4:3',   label: '4:3',   css: '4/3' },
    { key: '9:16',  label: '9:16',  css: '9/16' },
    { key: 'auto',  label: 'Auto',  css: 'auto' },
  ];

  let { presentation }: Props = $props();
  let currentRatio = $state<AspectRatio>('16:9');
  let menuOpen = $state(false);
  let isFullscreen = $state(false);
  let viewerEl: HTMLDivElement | undefined = $state();
  let iframeEl: HTMLIFrameElement | undefined = $state();

  function selectRatio(ratio: AspectRatio) {
    currentRatio = ratio;
    menuOpen = false;
  }

  function toggleFullscreen() {
    // iframe 자체를 fullscreen — slide content 가 화면 가득 (헤더 바 없이)
    const target = iframeEl || viewerEl;
    if (!target) return;
    if (!document.fullscreenElement) {
      target.requestFullscreen();
      isFullscreen = true;
    } else {
      document.exitFullscreen();
      isFullscreen = false;
    }
  }

  function handleFullscreenChange() {
    isFullscreen = !!document.fullscreenElement;
  }

  function handleClickOutside(e: MouseEvent) {
    if (menuOpen) menuOpen = false;
  }

  $effect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  });
</script>

<svelte:head>
  <title>{presentation.title} | AX Hub</title>
  {#if presentation.description}
    <meta name="description" content={presentation.description} />
  {/if}
</svelte:head>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="viewer-root"
  bind:this={viewerEl}
  onclick={handleClickOutside}
>
  <!-- Header -->
  <header class="viewer-header">
    <div class="header-inner">
      <div class="header-left">
        <a href="/pt" class="back-btn" aria-label="Back to presentations">
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </a>
        <div class="header-info">
          <h1 class="header-title">{presentation.title}</h1>
          <p class="header-author">@{presentation.user.username}</p>
        </div>
      </div>

      <div class="header-right">
        <!-- Aspect Ratio Menu -->
        <div class="menu-wrapper">
          <button
            class="option-btn"
            onclick={(e) => { e.stopPropagation(); menuOpen = !menuOpen; }}
            aria-label="Display options"
          >
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
              <rect x="3" y="3" width="7" height="7" rx="1" opacity="0.6" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" opacity="0.6" />
            </svg>
            <span class="option-label">{currentRatio}</span>
          </button>

          {#if menuOpen}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div class="menu-dropdown" onclick={(e) => e.stopPropagation()}>
              <div class="menu-title">Aspect Ratio</div>
              {#each RATIOS as ratio}
                <button
                  class="menu-item"
                  class:active={currentRatio === ratio.key}
                  onclick={() => selectRatio(ratio.key)}
                >
                  <span class="ratio-preview" style="aspect-ratio: {ratio.css}"></span>
                  <span>{ratio.label}</span>
                  {#if currentRatio === ratio.key}
                    <svg class="check-icon" width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  {/if}
                </button>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Fullscreen toggle -->
        <button class="option-btn" onclick={toggleFullscreen} aria-label="Toggle fullscreen">
          {#if isFullscreen}
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9L4 4m0 0v4m0-4h4m6 6l5 5m0 0v-4m0 4h-4M9 15l-5 5m0 0h4m-4 0v-4m11-6l5-5m0 0h-4m4 0v4" />
            </svg>
          {:else}
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          {/if}
        </button>
      </div>
    </div>
  </header>

  <!-- Presentation Area -->
  <div class="viewer-body">
    {#if currentRatio === 'auto'}
      <iframe
        bind:this={iframeEl}
        src={presentation.raw_url}
        title={presentation.title}
        class="iframe-auto"
        allow="fullscreen"
        allowfullscreen
      ></iframe>
    {:else}
      <div class="iframe-container" style="aspect-ratio: {RATIOS.find(r => r.key === currentRatio)?.css}">
        <iframe
          bind:this={iframeEl}
          src={presentation.raw_url}
          title={presentation.title}
          class="iframe-fixed"
          allow="fullscreen"
          allowfullscreen
        ></iframe>
      </div>
    {/if}
  </div>
</div>

<style>
  .viewer-root {
    min-height: 100vh;
    background: #0a0a0a;
    display: flex;
    flex-direction: column;
  }

  /* Header */
  .viewer-header {
    background: #141414;
    border-bottom: 1px solid #222;
    flex-shrink: 0;
    z-index: 10;
    padding: 0 12px;
  }

  .header-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1280px;
    margin: 0 auto;
    padding: 8px 0;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .back-btn {
    color: #888;
    flex-shrink: 0;
    padding: 4px;
    transition: color 0.15s;
  }
  .back-btn:hover { color: #fff; }

  .header-info { min-width: 0; }

  .header-title {
    font-size: 13px;
    font-weight: 500;
    color: #eee;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .header-author {
    font-size: 11px;
    color: #666;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  /* Option buttons */
  .option-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    color: #888;
    background: none;
    border: 1px solid #333;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s;
  }
  .option-btn:hover {
    color: #fff;
    border-color: #555;
  }

  .option-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
  }

  /* Dropdown menu */
  .menu-wrapper {
    position: relative;
  }

  .menu-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    background: #1a1a1a;
    border: 1px solid #333;
    min-width: 160px;
    z-index: 50;
    box-shadow: 0 8px 24px rgba(0,0,0,0.5);
  }

  .menu-title {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #555;
    padding: 8px 12px 4px;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    color: #aaa;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.1s;
  }
  .menu-item:hover {
    background: #252525;
    color: #fff;
  }
  .menu-item.active {
    color: #fff;
  }

  .ratio-preview {
    display: block;
    width: 20px;
    max-height: 20px;
    border: 1.5px solid currentColor;
    opacity: 0.5;
  }

  .check-icon {
    margin-left: auto;
    color: #4ade80;
  }

  /* Viewer body */
  .viewer-body {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
    padding: 12px;
    overflow: hidden;
  }

  .iframe-auto {
    width: 100%;
    height: 100%;
    border: none;
    background: #fff;
    min-height: calc(100vh - 56px);
  }

  .iframe-container {
    width: 100%;
    max-width: 100%;
    max-height: calc(100vh - 56px - 24px);
    background: #fff;
    position: relative;
    overflow: hidden;
    margin: auto;
  }

  .iframe-fixed {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  /* Responsive: on narrow screens, shrink container width to maintain ratio */
  @media (max-width: 768px) {
    .viewer-header {
      padding: 6px 8px;
    }
    .header-title {
      font-size: 12px;
      max-width: 180px;
    }
    .option-label {
      display: none;
    }
    .viewer-body {
      padding: 4px;
    }
  }
</style>
